import { useEffect, useState } from "react";
import type { EvaluationScore } from "@chessla/shared";
import { normalizeEvaluation, type Evaluation } from "@/shared/components/evaluation";
import { liveEval, positionEvaluationScore } from "./live-eval";

const PROGRESSIVE_EVALUATION_DEPTHS = [2, 4, 8, 16, 24] as const;

export const progressiveEvaluationDepths = (maxDepth = 12): number[] => {
  const bounded = Math.max(2, Math.floor(maxDepth));
  return [...PROGRESSIVE_EVALUATION_DEPTHS.filter((depth) => depth < bounded), bounded];
};

type Analyze = (fen: string, depth: number, signal: AbortSignal) => Promise<EvaluationScore | null>;

export const runProgressiveEvaluation = async ({
  fen,
  signal,
  analyze,
  onEvaluation,
  maxDepth = 12,
}: {
  fen: string;
  signal: AbortSignal;
  analyze: Analyze;
  onEvaluation: (evaluation: EvaluationScore, depth: number) => void;
  maxDepth?: number;
}): Promise<EvaluationScore | null> => {
  let latest: EvaluationScore | null = null;
  for (const depth of progressiveEvaluationDepths(maxDepth)) {
    if (signal.aborted) break;
    try {
      const evaluation = await analyze(fen, depth, signal);
      if (signal.aborted) break;
      if (evaluation) {
        latest = evaluation;
        onEvaluation(evaluation, depth);
      }
    } catch {
      if (signal.aborted) break;
    }
  }
  return latest;
};

interface ProgressiveEvaluationState {
  fen: string;
  evaluation: Evaluation | null;
  pending: boolean;
}

export const useProgressiveEvaluation = ({
  fen,
  enabled,
  maxDepth = 12,
}: {
  fen: string;
  enabled: boolean;
  maxDepth?: number;
}): { evaluation: Evaluation | null; pending: boolean } => {
  const [state, setState] = useState<ProgressiveEvaluationState | null>(null);

  useEffect(() => {
    if (!enabled) {
      setState({ fen, evaluation: null, pending: false });
      return;
    }
    const controller = new AbortController();
    const sideToMove = fen.split(" ")[1] === "b" ? "b" : "w";
    setState({ fen, evaluation: null, pending: true });
    void runProgressiveEvaluation({
      fen,
      signal: controller.signal,
      analyze: async (position, depth, signal) => {
        const result = await liveEval.analyze(position, depth, signal);
        return result ? positionEvaluationScore(result) : null;
      },
      onEvaluation: (evaluation) => {
        setState({ fen, evaluation: normalizeEvaluation(evaluation, sideToMove), pending: false });
      },
      maxDepth,
    }).then((evaluation) => {
      if (!controller.signal.aborted && !evaluation) setState({ fen, evaluation: null, pending: false });
    });
    return () => controller.abort();
  }, [enabled, fen, maxDepth]);

  const exact = state?.fen === fen ? state : null;
  return {
    evaluation: enabled ? (exact?.evaluation ?? null) : null,
    pending: enabled && (exact?.pending ?? true),
  };
};
